/*eslint-disable*/
import { EditorState, convertToRaw } from 'draft-js'
import produce from 'immer'
import * as R from 'ramda'

import { getValuesBetweenBraces } from '@/Flows/_utils'
import { ErrorHandlingFailIcon, ErrorHandlingSuccessIcon, NoTriggerIcon } from '@/common/Icons/Workflow.Icons'

import { generateUniqueId } from '@/common/utils/utils'
import { onPremConnectionListResponseT, onPremListResponseT } from '../onPremTypes'
import { ActionPanelStateT } from './ActionPanel'
import { checkNodeInputHasValue } from './CanvasLeftPanel'
import {
    configKinds,
    converToEditorFormat,
    convertAsaSingleString,
    convertToServerFormat,
    emptyEditorState,
    getInputsInNestedLevel,
    isBinaryOperatorValue
} from './utils'
import { ApptriggerIconNew, AppTriggersIcon, WebhookIcon } from '@/common/Icons/Icons'
import { AppTriggerPanelStateT } from './AppTriggerPanel'
import { getProductId } from '@/common/utils/api-utils'
import { AssistantT } from '@/Assistant/Construction/kind'
import { TeamsWorkspaceT, WorkspaceT } from '@/Integrations/types'

// making fields as optional bcz it's not needed while saving otherwise it will show error in save node function every panel
// but it must need to be provided while creating or editing action
// it's like mandatory

export type InputDictionary = {
    kind: 'dictionary'
    fields?: dictionaryFieldT[]
}

export type dictionaryFieldT = {
    key: string
    value: EditorState | string
}

export const Tree: NodeT = {
    id: '',
    name: '',
    icon: '',
    input: [],
    output: {},
    description: '',
    kind: 'Action',
    path: []
}

export const TriggerTree: NodeT = {
    id: '',
    name: '',
    icon: '',
    input: [],
    output: {},
    description: '',
    kind: 'Action',
    path: []
}

export const EndNode: NodeT = {
    id: '',
    name: '',
    icon: '',
    input: [],
    output: {},
    description: '',
    kind: 'End',
    path: []
}

export enum NodeMode {
    View,
    Add,
    Edit,
    Loading,
    Error,
    Delete
}

export const NodeAddModeT: NodeAddT = {
    mode: NodeMode.Add,
};

export type NodeViewT = {
    mode: NodeMode.View
}

export type NodeAddT = {
    mode: NodeMode.Add
}

export type NodeEditT = {
    mode: NodeMode.Edit
    nodeEditInput: NodeT
    nodeOutputConfig: ActionInputsListT[]
}

export type NodeDeleteT = {
    mode: NodeMode.Delete
    nodeEditInput: NodeT
    currentNode: TreeT<NodeT>
    loading: boolean
    parentNode: NodeT
    error: {
        error: boolean
        info: string
    }
}

export type PopupLoadingT = {
    mode: NodeMode.Loading
}

export type PopupErrorT = {
    mode: NodeMode.Error
    error: string
}

export type NodeModes = NodeViewT | NodeAddT | NodeEditT | PopupLoadingT | PopupErrorT | NodeDeleteT

export const NodeView: NodeViewT = {
    mode: NodeMode.View
}

export const NodeAddT: NodeAddT = {
    mode: NodeMode.Add
}

export const NodeEdit: NodeEditT = {
    mode: NodeMode.Edit,
    nodeEditInput: Tree,
    nodeOutputConfig: []
}

export const NodeDelete: NodeDeleteT = {
    mode: NodeMode.Delete,
    nodeEditInput: Tree,
    loading: false,
    parentNode: Tree,
    currentNode: { value: Tree, children: [] },
    error: {
        error: false,
        info: ''
    }
}

export const PopupLoading: PopupLoadingT = {
    mode: NodeMode.Loading
}

export const PopupError: PopupErrorT = {
    mode: NodeMode.Error,
    error: ''
}

export type ContextPopupStateT = {
    show: boolean
    inputWithValues: ContextInputValueTypeT[]
    currentInput: string
    collectionPicker: CollectionPickerT
    isMultiple: IsMultipleT
    isFilePicker?: boolean
    currentInputIndex?: number
}

type CollectionPickerT = {
    isCollectionPicker: boolean
    selectedCollection: {
        name: string
        value: CollectionOutputListTypeT
    }
    collectionType: InputFieldT
    app: ResponseOutputT & PathIdType
}

type IsMultipleT = {
    path: string
    parentKey: string
}

type MultipleValueInputsT = CollectionPickerT & IsMultipleT

type ContextValueTypeT = {
    id: string
    value: string
    type: 'context'
    valuePath: string[]
    icon: string
}

type StringValueTypeT = { type: 'string'; value: string }

export type ValueTypeT = ContextValueTypeT | StringValueTypeT

export type ContextInputValueTypeT = {
    nodeInput: string
    value: ValueTypeT[]
    multipleValueInputs: MultipleValueInputsT
}

export type RightPanelStateT = {
    show: boolean
    parentNode: TreeT<NodeT>
    mode: NodeModes
    multiActionAccess: 'yes' | 'no' | 'loading'
    approvalAccess: 'yes' | 'no' | 'loading'
    sendNotificationAccess: 'yes' | 'no' | 'loading'
    currentNode:
        | ActionsResponseT
        | TriggerResponseT
        | WebHookResponseT
        | ConditionResponseT
        | ApprovalResponseT
        | AppTriggerResponseT
        | FunctionResponseT
        | ForEachResponseT
        | EndResponseT
        | TimerResponseT
        | NotificationResponseT
    currentTrigger: "application" | "webhook" |"no_trigger";
}

export type LeftPanelStateT = {
    show: boolean
    step1: {
        done: boolean
    }
    step2: {
        done: boolean
    }
    step3: {
        done: boolean
    }
    parentNode: TreeT<NodeT>
    mode: NodeModes
    currentNode:
        | ActionsResponseT
        | ConditionResponseT
        | FunctionResponseT
        | AppTriggerResponseT
        | ForEachResponseT
        | EndResponseT
        | ApprovalResponseT
        | WebHookResponseT
        | TimerResponseT
        | NotificationResponseT
}

export type RightPanelStateContext = {
    rightPanelState: RightPanelStateT
    setRightPanelState: (object: any) => void
    saveTree: (node: any) => void
    editTreeData: any
    setEditTreeData: (node: any) => void
    treeData: any
    setTreeData: (node: any) => void
    disableDeployButton: boolean[]
    setDisableDeployButton: React.Dispatch<React.SetStateAction<boolean[]>>
    setAutomationMetaType: (type: 'trigger' | 'no_trigger') => void
    active_automation: boolean
    hasLinkedDialogs: boolean
    automationMeta:AutomationMeta
}

export type LeftPanelStateContextT = {
    leftPanelState: LeftPanelStateT
    setLeftPanelState: (object: any) => void
}

export enum FlowInput {
    Name = 'flowName',
    Description = 'flowDescription'
}

export type SaveAs = {
    showSaveAsPopup: boolean
    flowName: string
    flowDetail: string
}

export type FlowDetailS = {
    id: string | null
    url: string
    toFlow: boolean
    saveAs: SaveAs
}

export const initialSaveAsData = {
    showSaveAsPopup: false,
    flowName: '',
    flowDetail: ''
}

type NodeType =
    | 'Action'
    | 'AppTrigger'
    | 'Approval'
    | 'EventTrigger'
    | 'Logic'
    | 'Function'
    | 'ForEach'
    | 'Condition'
    | 'Repeat'
    | 'NoTrigger'
    | 'End'
    | 'Setup'
    | 'Notification'
    | 'EmptyAction'
    | 'ParallelPath'
    | 'EmptyCondition'
    | 'EmptyFunction'
    | 'EmptyForEach'
    | 'EmptyApproval'
    | 'ErrorHandlingCondition'
    | 'WebhookTrigger'
    | 'EmptyTimer'
    | 'Timer'
    | 'EmptyNotification'

export type NodeT = {
    id: string
    name: string
    icon: any
    input: InputFieldT[] | (InputRulesArray | hasCombinatorT)[]
    output: any
    description: string
    kind: NodeType
    interval?:number
    meta_data?: any
    path: {
        actionId: string
        nodeId: string
        forEachId?: string
        forEachName?: string
        forEachNodeId?: string
        approvalId?: string
        forEachSubflow?: {
            actionId: string
            nodeId: string
            forEachId?: string
            forEachName?: string
            forEachNodeId?: string
        }[]
    }[]
    subflow?: { children: TreeT<NodeT>[] }
}

export interface TreeT<T> {
    value: T
    children: Array<TreeT<T>>
}

export function mapTree<T, U>(fa: TreeT<T>, f: (a: T) => U): TreeT<U> {
    return {
        value: f(fa.value),
        children: fa.children.map(t => mapTree(t, f))
    } as TreeT<U>
}
export function mapTreeWithIndex<T, U>(fa: TreeT<T>, f: (a: T, i?: number) => U, index: number = 1): TreeT<U> {
    return {
        value: f(fa.value, index),
        children: fa.children.map((t, i) => mapTreeWithIndex(t, f, index + i + 1))
    } as TreeT<U>
}

export const reduceTree = <A, B>(fa: TreeT<A>, b: B, f: (b: B, a: A) => B): B => {
    let r: B = f(b, fa.value)
    const len = fa.children.length
    for (let i = 0; i < len; i++) {
        r = reduceTree(fa.children[i], r, f)
    }
    return r
}

export function mapTreeWithPath<T, U>(fa: TreeT<T>, f: (a: T, i?: string) => U, index: string = '1'): TreeT<U> {
    return {
        value: f(fa.value, index),
        children: fa.children.map((t, i) => mapTreeWithPath(t, f, `${index}.${i + 1}`))
    } as TreeT<U>
}

export function addNode(
    node: NodeT,
    parentNode: NodeT,
    tree: TreeT<NodeT>,
    errorHandling: { isErrorHandling: boolean; errorHandlingChilds: TreeT<NodeT>[] },
    addingInsideForEach?: { addingInsideForEach: boolean; forEachNodeId: string; parentNode: NodeT }
) {
    const clonedTree = R.clone(tree)
    if (
        clonedTree.value.id == parentNode.id ||
        (addingInsideForEach && addingInsideForEach.forEachNodeId == clonedTree.value.id)
    ) {
        if (!addingInsideForEach || !addingInsideForEach.addingInsideForEach) {
            const newTree = {
                value: node,
                children: clonedTree.children
            }

            const x = errorHandling.isErrorHandling
                ? [{ ...newTree, children: errorHandling.errorHandlingChilds }]
                : [newTree]

            clonedTree.children = x
        } else {
            const newTree = {
                value: node,
                children: []
            }

            const subflow = clonedTree.value.subflow as { children: TreeT<NodeT>[] }

            // check wheather for each is parent
            if (addingInsideForEach.forEachNodeId == addingInsideForEach.parentNode.id) {
                // const x = errorHandling.isErrorHandling
                // ? [...subflow.children, { ...newTree, children: errorHandling.errorHandlingChilds }]
                // : [...subflow.children, newTree]

                const x = errorHandling.isErrorHandling
                    ? [{ ...newTree, children: errorHandling.errorHandlingChilds }]
                    : [{ ...newTree, children: subflow.children }]

                subflow.children = x

                clonedTree.value = { ...clonedTree.value, subflow: subflow }
            } else {
                subflow.children = subflow.children.map(x =>
                    addNode(node, addingInsideForEach.parentNode, x, errorHandling, {
                        ...addingInsideForEach,
                        addingInsideForEach: false
                    })
                )

                clonedTree.value = { ...clonedTree.value, subflow: subflow }
            }
        }
    } else {
        clonedTree.children = clonedTree.children.map(x =>
            addNode(node, parentNode, x, errorHandling, addingInsideForEach)
        )
    }
    return clonedTree
}

export function addParallelPath(
    node: NodeT,
    parentNode: NodeT,
    tree: TreeT<NodeT>,
    addingInsideForEach?: { addingInsideForEach: boolean; forEachNodeId: string; parentNode: NodeT }
) {
    const clonedTree = R.clone(tree)
    if (
        clonedTree.value.id == parentNode.id ||
        (addingInsideForEach && addingInsideForEach.forEachNodeId == clonedTree.value.id)
    ) {
        const newTree = {
            value: node,
            children: []
        }

        if (!addingInsideForEach || !addingInsideForEach.addingInsideForEach) {
            if (clonedTree.children.length === 0) {
                const x = [
                    { ...newTree, value: { ...newTree.value, id: generateUniqueId() } },
                    { ...newTree, value: { ...newTree.value, id: generateUniqueId() } }
                ]
                clonedTree.children = x
            } else {
                clonedTree.children = [
                    ...clonedTree.children,
                    { ...newTree, value: { ...newTree.value, id: generateUniqueId() } }
                ]
            }
        } else {
            const subflow = clonedTree.value.subflow as { children: TreeT<NodeT>[] }

            if (addingInsideForEach.forEachNodeId == addingInsideForEach.parentNode.id) {
                const x =
                    subflow.children.length == 0
                        ? [
                              { ...newTree, value: { ...newTree.value, id: generateUniqueId() } },
                              { ...newTree, value: { ...newTree.value, id: generateUniqueId() } }
                          ]
                        : [...subflow.children, { ...newTree, value: { ...newTree.value, id: generateUniqueId() } }]

                subflow.children = x

                clonedTree.value = { ...clonedTree.value, subflow: subflow }
            } else {
                subflow.children = subflow.children.map(x =>
                    addParallelPath(node, parentNode, x, {
                        ...addingInsideForEach,
                        addingInsideForEach: false
                    })
                )

                clonedTree.value = { ...clonedTree.value, subflow: subflow }
            }
        }
    } else {
        clonedTree.children = clonedTree.children.map(x => addParallelPath(node, parentNode, x, addingInsideForEach))
    }
    return clonedTree
}

export function addErrorHandlingNodesInAppTrigger(
    node: NodeT,
    parentNode: NodeT,
    children: TreeT<NodeT>[],
    appTriggerId:string,
    currentNode: AppTriggerResponseT,
    tree:TreeT<NodeT>
){
    const clonedTree = R.clone(tree)

    if (clonedTree.value.id == parentNode.id){
        const newTree = {
            value: node,
            children: []
        }

        const childs = [
            {
                children,
                value: {
                    ...newTree.value,
                    id: generateUniqueId(),
                    icon: ErrorHandlingSuccessIcon,
                    name: `Success`,
                    input: [
                        {
                            left: '${' + `${appTriggerId}` + '.success}',
                            operator: 'is_true',
                            right: '',
                            type: 'operator',
                            valType: 'boolean'
                        }
                    ] as InputRulesArray[],
                    description: 'Success Is True',
                    meta_data: {
                        parent_id: appTriggerId,
                        action_id: currentNode.selectedAction.id,
                        app_id: currentNode.selectedApp.id,
                        subflow: { hasSubFlow: false }
                    }
                }
            },
            {
                ...newTree,
                value: {
                    ...newTree.value,
                    id: generateUniqueId(),
                    icon: ErrorHandlingFailIcon,
                    name: `Failed`,
                    input: [
                        {
                            left: '${' + `${appTriggerId}` + '.success}',
                            operator: 'is_false',
                            right: '',
                            type: 'operator',
                            valType: 'boolean'
                        }
                    ] as InputRulesArray[],
                    description: 'Success Is False',
                    path: node.path.filter((path, i) => node.path.length != i + 1),
                    meta_data: {
                        parent_id: appTriggerId,
                        action_id: currentNode.selectedAction.id,
                        app_id: currentNode.selectedApp.id,
                        subflow: { hasSubFlow: false }
                    }
                }
            }
        ]

        clonedTree.children = childs
    }else {
        clonedTree.children = clonedTree.children.map(x =>
            addErrorHandlingNodesInAppTrigger(node, parentNode, children,appTriggerId,currentNode,x)
        )
    }
    return clonedTree
  
}

export function deleteErrorHandlingNodes(
    appTriggerId:string,
    parentNode: NodeT,
    tree:TreeT<NodeT>
){
    const clonedTree = R.clone(tree)
    
    if (clonedTree.value.id == parentNode.id){
        clonedTree.children = clonedTree.children.reduce((accum,child)=>{
            if(child.value.meta_data.parent_id === appTriggerId){
                return [...accum,...child.children]
            }else return [...accum, child]
        },[] as TreeT<NodeT>[]) 
    }else {
        clonedTree.children = clonedTree.children.map(x =>
            deleteErrorHandlingNodes(appTriggerId,parentNode,x)
        )
    }
    return clonedTree

}

export function addErrorHandlingNodes(
    node: NodeT,
    parentNode: NodeT,
    tree: TreeT<NodeT>,
    currentNode: ActionsResponseT ,
    errorNodes: {
        multipleNodes: boolean
        name: string
        mode: NodeMode
        actionState: ActionPanelStateT | AppTriggerPanelStateT
        addingChildToActionState: (object: any) => void
    },
    addingInsideForEach: {
        addingInsideForEach: boolean
        forEachNodeId: string
        parentNode: NodeT
    }
) {
    const clonedTree = R.clone(tree)

    const { multipleNodes, name } = errorNodes

    if (
        clonedTree.value.id == parentNode.id ||
        (addingInsideForEach && addingInsideForEach.forEachNodeId == clonedTree.value.id)
    ) {
        const newTree = {
            value: node,
            children: []
        }

        const childs = [
            {
                ...newTree,
                value: {
                    ...newTree.value,
                    id: generateUniqueId(),
                    icon: ErrorHandlingSuccessIcon,
                    name: `Success`,
                    input: [
                        {
                            left: '${' + `${parentNode.id}` + '.success}',
                            operator: 'is_true',
                            right: '',
                            type: 'operator',
                            valType: 'boolean'
                        }
                    ] as InputRulesArray[],
                    description: 'Success Is True',
                    meta_data: {
                        parent_id: parentNode.id,
                        action_id: currentNode.selectedAction.id,
                        app_id: currentNode.selectedApp.id,
                        subflow:
                            parentNode.meta_data && parentNode.meta_data.subflow
                                ? parentNode.meta_data.subflow
                                : { hasSubFlow: false }
                    }
                }
            },
            {
                ...newTree,
                value: {
                    ...newTree.value,
                    id: generateUniqueId(),
                    icon: ErrorHandlingFailIcon,
                    name: `Failed`,
                    input: [
                        {
                            left: '${' + `${parentNode.id}` + '.success}',
                            operator: 'is_false',
                            right: '',
                            type: 'operator',
                            valType: 'boolean'
                        }
                    ] as InputRulesArray[],
                    description: 'Success Is False',
                    // path: node.path.filter((path, i) => node.path.length != i + 1)                    
                    meta_data: {
                        parent_id: parentNode.id,
                        action_id: currentNode.selectedAction.id,
                        app_id: currentNode.selectedApp.id,
                        subflow:
                            parentNode.meta_data && parentNode.meta_data.subflow
                                ? parentNode.meta_data.subflow
                                : { hasSubFlow: false }
                    }
                }
            }
        ]

        if (!addingInsideForEach || !addingInsideForEach.addingInsideForEach) {
            if (clonedTree.children.length === 0) {
                clonedTree.children = multipleNodes
                    ? childs
                    : childs.filter(node => node.value.name.toLowerCase() == name.toLowerCase())

                const nextState = produce(errorNodes.actionState, draft => {
                    draft.currentNode.errorHandling.errorHandlingChildrenForAddMode = childs
                })
                errorNodes.mode == NodeMode.Add && errorNodes.addingChildToActionState(nextState)
            } else {
                const hasOneErrorHandlingChild = clonedTree.children.filter(
                    err => err.value.name.toLowerCase() == 'success' || err.value.name.toLowerCase() == 'failed'
                )

                if (multipleNodes) {
                    const errorHandlingWithChilds = childs.map(node => {
                        if (node.value.name.toLowerCase() == 'success') {
                            return { ...node, children: clonedTree.children }
                        }

                        return node
                    })
                    clonedTree.children = errorHandlingWithChilds

                    const nextState = produce(errorNodes.actionState, draft => {
                        draft.currentNode.errorHandling.errorHandlingChildrenForAddMode = errorHandlingWithChilds
                    })

                    errorNodes.mode == NodeMode.Add && errorNodes.addingChildToActionState(nextState)
                } else if (hasOneErrorHandlingChild.length == 0) {
                    clonedTree.children = [
                        ...(name.toLowerCase() != 'success' ? clonedTree.children : []),
                        ...childs
                            .map(node => {
                                if (node.value.name.toLowerCase() == 'success') {
                                    return { ...node, children: clonedTree.children }
                                }

                                return node
                            })
                            .filter(node => node.value.name.toLowerCase() == name.toLowerCase())
                    ]
                } else if (hasOneErrorHandlingChild[0].value.name.toLowerCase() == 'success') {
                    clonedTree.children = clonedTree.children.concat(
                        childs.filter(node => node.value.name.toLowerCase() == 'failed')
                    )
                } else {
                    clonedTree.children = [
                        ...childs
                            .filter(node => node.value.name.toLowerCase() == 'success')
                            .map(node => {
                                return {
                                    ...node,
                                    children: clonedTree.children.filter(
                                        child => child.value.name.toLowerCase() != 'failed'
                                    )
                                }
                            }),
                        hasOneErrorHandlingChild[0]
                    ]
                }
            }
        } else {
            const subflow = clonedTree.value.subflow as { children: TreeT<NodeT>[] }

            subflow.children = subflow.children.map(x =>
                addErrorHandlingNodes(node, parentNode, x, currentNode, errorNodes, {
                    ...addingInsideForEach,
                    addingInsideForEach: false
                })
            )

            clonedTree.value = { ...clonedTree.value, subflow: subflow }
        }
    } else {
        clonedTree.children = clonedTree.children.map(x =>
            addErrorHandlingNodes(node, parentNode, x, currentNode, errorNodes, addingInsideForEach)
        )
    }
    return clonedTree
}

export function addNewNodeAsParentToParallelNode(
    newNode: NodeT,
    parentNode: NodeT,
    replaceNode: NodeT,
    tree: TreeT<NodeT>,
    errorHandling: { isErrorHandling: boolean; errorHandlingChilds: TreeT<NodeT>[] },
    addingInsideForEach?: { addingInsideForEach: boolean; forEachNodeId: string; parentNode: NodeT }
) {
    const clonedTree = R.clone(tree)
    if (
        clonedTree.value.id == parentNode.id ||
        (addingInsideForEach && addingInsideForEach.forEachNodeId == tree.value.id)
    ) {
        if (!addingInsideForEach || !addingInsideForEach.addingInsideForEach) {
            const x = clonedTree.children.map(y => {
                if (y.value.id === replaceNode.id) {
                    const x = errorHandling.isErrorHandling
                        ? [
                              ...errorHandling.errorHandlingChilds.map(node => {
                                  if (
                                      node.value.kind == 'ErrorHandlingCondition' &&
                                      node.value.name.toLowerCase() == 'success'
                                  ) {
                                      return { ...node, children: [y] }
                                  }

                                  return node
                              })
                          ]
                        : [y]
                    return {
                        value: newNode,
                        children: x
                    }
                }

                return y
            })
            clonedTree.children = x
        } else {
            const subflow = tree.value.subflow as { children: TreeT<NodeT>[] }
            if (parentNode.kind == 'ForEach') {
                subflow.children = subflow.children.map(y => {
                    if (y.value.id === replaceNode.id) {
                        const x = errorHandling.isErrorHandling
                            ? [
                                  ...errorHandling.errorHandlingChilds.map(node => {
                                      if (
                                          node.value.kind == 'ErrorHandlingCondition' &&
                                          node.value.name.toLowerCase() == 'success'
                                      ) {
                                          return { ...node, children: [y] }
                                      }

                                      return node
                                  })
                              ]
                            : [y]
                        return {
                            value: newNode,
                            children: x
                        }
                    }

                    return y
                })
            } else {
                const x = subflow.children.map(x =>
                    addNewNodeAsParentToParallelNode(newNode, parentNode, replaceNode, x, errorHandling, {
                        ...addingInsideForEach,
                        addingInsideForEach: false
                    })
                )

                subflow.children = x
            }

            clonedTree.value = { ...clonedTree.value, subflow: subflow }
        }
    } else {
        clonedTree.children = clonedTree.children.map(x =>
            addNewNodeAsParentToParallelNode(newNode, parentNode, replaceNode, x, errorHandling, addingInsideForEach)
        )
    }
    return clonedTree
}

export function updateParallelPathWithChildren(
    node: NodeT,
    parentNode: NodeT,
    emptysibling: NodeT,
    emptysiblingPosition: number,
    tree: TreeT<NodeT>,
    errorHandling: { isErrorHandling: boolean; errorHandlingChilds: TreeT<NodeT>[] },
    addingInsideForEach?: { addingInsideForEach: boolean; forEachNodeId: string; parentNode: NodeT }
) {
    const clonedTree = R.clone(tree)

    if (
        clonedTree.value.id == parentNode.id ||
        (addingInsideForEach && addingInsideForEach.forEachNodeId == tree.value.id)
    ) {
        const newTree = {
            value: node,
            children: []
        }

        if (!addingInsideForEach || !addingInsideForEach.addingInsideForEach) {
            const childs =
                emptysiblingPosition === 0
                    ? [
                          {
                              ...newTree,
                              children: errorHandling.isErrorHandling ? errorHandling.errorHandlingChilds : []
                          },
                          { ...newTree, value: emptysibling }
                      ]
                    : [
                          { ...newTree, value: emptysibling },
                          {
                              ...newTree,
                              children: errorHandling.isErrorHandling ? errorHandling.errorHandlingChilds : []
                          }
                      ]

            clonedTree.children = childs
        } else {
            const subflow = tree.value.subflow as { children: TreeT<NodeT>[] }

            // check wheather for each is parent
            if (addingInsideForEach.forEachNodeId == addingInsideForEach.parentNode.id) {
                const childs =
                    emptysiblingPosition === 0
                        ? [
                              {
                                  ...newTree,
                                  children: errorHandling.isErrorHandling ? errorHandling.errorHandlingChilds : []
                              },
                              { ...newTree, value: emptysibling }
                          ]
                        : [
                              { ...newTree, value: emptysibling },
                              {
                                  ...newTree,
                                  children: errorHandling.isErrorHandling ? errorHandling.errorHandlingChilds : []
                              }
                          ]

                subflow.children = childs

                clonedTree.value = { ...clonedTree.value, subflow: subflow }
            } else {
                subflow.children = subflow.children.map(x =>
                    updateParallelPathWithChildren(
                        node,
                        parentNode,
                        emptysibling,
                        emptysiblingPosition,
                        x,
                        errorHandling,
                        {
                            ...addingInsideForEach,
                            addingInsideForEach: false
                        }
                    )
                )

                clonedTree.value = { ...clonedTree.value, subflow: subflow }
            }
        }
    } else {
        clonedTree.children = clonedTree.children.map(x =>
            updateParallelPathWithChildren(
                node,
                parentNode,
                emptysibling,
                emptysiblingPosition,
                x,
                errorHandling,
                addingInsideForEach
            )
        )
    }
    return clonedTree
}

export function updateNode(
    node: NodeT,
    tree: TreeT<NodeT>,
    addingInsideForEach?: { addingInsideForEach: boolean; forEachNodeId: string; parentNode: NodeT }
) {
    if (tree.value.id == node.id || (addingInsideForEach && addingInsideForEach.forEachNodeId == tree.value.id)) {
        if (!addingInsideForEach || !addingInsideForEach.addingInsideForEach) {
            if (tree.value.kind == 'ForEach') {
                tree.value = { ...node, subflow: tree.value.subflow }
            } else {
                tree.value = node
            }
        } else {
            const subflow = tree.value.subflow as { children: TreeT<NodeT>[] }

            subflow.children = subflow.children.map(x =>
                updateNode(node, x, { ...addingInsideForEach, addingInsideForEach: false })
            )

            tree.value = { ...tree.value, subflow: subflow }
        }
    } else {
        tree.children = tree.children.map(x => updateNode(node, x, addingInsideForEach))
    }
    return tree
}

export function removeChildrenPaths(node: NodeT, tree: TreeT<NodeT>) {
    let clonedTree = R.clone(tree)

    if (clonedTree.value.id == node.id) {
        clonedTree = removeNodePaths(clonedTree, node.id)
    } else {
        clonedTree.children = clonedTree.children.map(x => removeChildrenPaths(node, x))
    }
    return clonedTree
}

export function removeNodePaths(tree: TreeT<NodeT>, removePathId: string) {
    const clonedTree = R.clone(tree)

    clonedTree.value = { ...clonedTree.value, path: clonedTree.value.path.filter(x => x.nodeId !== removePathId) }

    clonedTree.children = clonedTree.children.map(child => {
        if (child.children.length === 0) {
            const updatedChild = produce(child, draftState => {
                draftState.value.path = child.value.path.filter(x => x.nodeId !== removePathId)
                if (draftState.value.kind == 'ForEach') {
                    const subflow = draftState.value.subflow as { children: TreeT<NodeT>[] }
                    draftState.value.subflow = {
                        children: subflow.children.map(child => removeNodePaths(child, removePathId))
                    }
                }
            })

            return updatedChild
        } else {
            const updatedChild = produce(child, draftState => {
                draftState.value.path = child.value.path.filter(x => x.nodeId !== removePathId)
                if (draftState.value.kind == 'ForEach') {
                    const subflow = draftState.value.subflow as { children: TreeT<NodeT>[] }
                    draftState.value.subflow = {
                        children: subflow.children.map(child => removeNodePaths(child, removePathId))
                    }
                }
            })

            return removeNodePaths(updatedChild, removePathId)
        }
    })

    return clonedTree as TreeT<NodeT>
}

export function updateNewNodePathsInChilds(id: string, tree: TreeT<NodeT>, path: { actionId: string; nodeId: string, hasErrorhandling:boolean }) {
    let clonedTree = R.clone(tree)

    if (clonedTree.value.id == id) {
        clonedTree.children = clonedTree.children.map(child => {
            return updatePathsInChilds(id, child, path)
        })
    } else {
        clonedTree.children = clonedTree.children.map(x => updateNewNodePathsInChilds(id, x, path))
    }
    return clonedTree
}

export function updatePathsInChilds(
    nodeId: string,
    child: TreeT<NodeT>,
    path: {
        actionId: string
        nodeId: string
        forEachId?: string | undefined
        forEachName?: string | undefined
    }
): TreeT<NodeT> {
    if (child.children.length === 0) {
        const filterePathIndex = child.value.path
            .map((p, i) => {
                if (p.nodeId == nodeId) {
                    return { ...p, index: i }
                }
                return null
            })
            .filter(Boolean)[0] as any

        if (child.value.kind == 'ForEach') {
            const subflow = child.value.subflow as { children: TreeT<NodeT>[] }
            return {
                value: {
                    ...child.value,
                    path: !filterePathIndex
                        ? [path, ...child.value.path]
                        : [
                              ...child.value.path.filter((p, i) => i <= filterePathIndex.index),
                              path,
                              ...child.value.path.filter((p, i) => i > filterePathIndex.index)
                          ],
                    subflow: { children: subflow.children.map(x => updatePathsInChilds(nodeId, x, path)) }
                },
                children: []
            }
        }

        return {
            value: {
                ...child.value,
                path: !filterePathIndex
                    ? [path, ...child.value.path]
                    : [
                          ...child.value.path.filter((p, i) => i <= filterePathIndex.index),
                          path,
                          ...child.value.path.filter((p, i) => i > filterePathIndex.index)
                      ]
            },
            children: []
        }
    } else {
        const filterePathIndex = child.value.path
            .map((p, i) => {
                if (p.nodeId == nodeId) {
                    return { ...p, index: i }
                }
                return null
            })
            .filter(Boolean)[0] as any

        if (child.value.kind == 'ForEach') {
            const subflow = child.value.subflow as { children: TreeT<NodeT>[] }
            return {
                value: {
                    ...child.value,
                    path: !filterePathIndex
                        ? [path, ...child.value.path]
                        : [
                              ...child.value.path.filter((p, i) => i <= filterePathIndex.index),
                              path,
                              ...child.value.path.filter((p, i) => i > filterePathIndex.index)
                          ],
                    subflow: { children: subflow.children.map(x => updatePathsInChilds(nodeId, x, path)) }
                },
                children: child.children.map(x => updatePathsInChilds(nodeId, x, path))
            }
        }

        return {
            value: {
                ...child.value,
                path: !filterePathIndex
                    ? [path, ...child.value.path]
                    : [
                          ...child.value.path.filter((p, i) => i <= filterePathIndex.index),
                          path,
                          ...child.value.path.filter((p, i) => i > filterePathIndex.index)
                      ]
            },
            children: child.children.map(x => updatePathsInChilds(nodeId, x, path))
        }
    }
}

export function updateNewActionPathsInChilds(
    id: string,
    tree: TreeT<NodeT>,
    path: { actionId: string; nodeId: string, hasErrorhandling:boolean }
) {
    let clonedTree = R.clone(tree)

    if (clonedTree.value.id == id) {
        clonedTree.children = clonedTree.children.map(child => {
            return updateActionPathsInChilds(id, child, path)
        })
    } else {
        clonedTree.children = clonedTree.children.map(x => updateNewActionPathsInChilds(id, x, path))
    }
    return clonedTree
}

export function updateActionPathsInChilds(
    nodeId: string,
    child: TreeT<NodeT>,
    path: { actionId: string; nodeId: string }
): TreeT<NodeT> {
    if (child.children.length === 0) {
        return {
            value: {
                ...child.value,
                path: child.value.path.map((oldPath, i) => {
                    if (oldPath.nodeId === nodeId) {
                        return path
                    }
                    return oldPath
                })
            },
            children: []
        }
    } else {
        return {
            value: {
                ...child.value,
                path: child.value.path.map((oldPath, i) => {
                    if (oldPath.nodeId === nodeId) {
                        return path
                    }
                    return oldPath
                })
            },
            children: child.children.map(x => updateActionPathsInChilds(nodeId, x, path))
        }
    }
}

export function updateForEachPathsInChilds(
    id: string,
    tree: TreeT<NodeT>,
    path: {
        actionId: string
        nodeId: string
        forEachId?: string | undefined
        forEachName?: string | undefined
    }
) {
    let clonedTree = R.clone(tree)

    if (clonedTree.value.id == id) {
        const subflow = clonedTree.value.subflow as { children: TreeT<NodeT>[] }
        clonedTree.value.subflow = { children: subflow.children.map(x => updateForEachPath(id, x, path)) }
    } else {
        clonedTree.children = clonedTree.children.map(x => updateForEachPathsInChilds(id, x, path))
    }
    return clonedTree
}

export function updateForEachPath(
    id: string,
    tree: TreeT<NodeT>,
    path: {
        actionId: string
        nodeId: string
        forEachId?: string | undefined
        forEachName?: string | undefined
    }
) {
    let clonedTree = R.clone(tree)

    clonedTree.value = {
        ...clonedTree.value,
        path: clonedTree.value.path.map(paths => {
            if (paths.nodeId == id) {
                return path
            }
            return paths
        })
    }
    clonedTree.children = clonedTree.children.map(x => updateForEachPath(id, x, path))

    return clonedTree
}

export function updateErrorHandlingMetaTree(id: string, tree: TreeT<NodeT>, currentNode: ActionsResponseT) {
    let clonedTree = R.clone(tree)

    if (clonedTree.value.id == id) {
        clonedTree.children = clonedTree.children.map(child => {
            if (child.value.kind == 'ErrorHandlingCondition') {
                return {
                    ...child,
                    value: {
                        ...child.value,
                        meta_data: {
                            ...child.value.meta_data,
                            action_id: currentNode.selectedAction.id,
                            app_id: currentNode.selectedApp.id
                        }
                    }
                }
            }
            return child
        })
    } else {
        clonedTree.children = clonedTree.children.map(x => updateErrorHandlingMetaTree(id, x, currentNode))
    }
    return clonedTree
}

export function removeChildrenContextVariables(
    id: string,
    tree: TreeT<NodeT>,
    triggerMeta: InputKey[] | undefined,
    fromEdit: { errorHandling: boolean; action_id: string },
    triggerId?:string
) {
    let clonedTree = R.clone(tree)

    if (clonedTree.value.id == id) {
        if (clonedTree.value.kind == 'ForEach') {
            const subflow = (clonedTree.value.subflow as unknown) as { children: TreeT<NodeT>[] }
            clonedTree.value.subflow = {
                children: subflow.children.map(x =>
                    findContextVariableChilds(
                        id,
                        x,
                        triggerMeta,
                        fromEdit,
                        clonedTree.value.meta_data &&
                            clonedTree.value.meta_data.collectionForRemoveContextVariable &&
                            clonedTree.value.meta_data.collectionForRemoveContextVariable.includes(id)
                            ? clonedTree.value.id
                            : null
                    )
                )
            }
        } else {
            clonedTree.children = clonedTree.children.map(child => {
                const filteredChilds = findContextVariableChilds(id, child, triggerMeta, fromEdit, null,triggerId)
                if (child.value.kind == 'ForEach') {
                    const subflow = (child.value.subflow as unknown) as { children: TreeT<NodeT>[] }
                    const forEachIncludesDeleteNodeId =
                        child.value.meta_data &&
                        child.value.meta_data.collectionForRemoveContextVariable &&
                        child.value.meta_data.collectionForRemoveContextVariable.includes(id)
                    child = filteredChilds
                    child.value.subflow = {
                        children: subflow.children.map(x =>
                            findContextVariableChilds(
                                id,
                                x,
                                triggerMeta,
                                fromEdit,
                                forEachIncludesDeleteNodeId ? child.value.id : null
                            )
                        )
                    }
                    return child
                }
                return filteredChilds
            })
        }
    } else {
        clonedTree.children = clonedTree.children.map(x => removeChildrenContextVariables(id, x, triggerMeta, fromEdit))
    }
    return clonedTree
}

export function findContextVariableChilds(
    nodeId: string,
    child: TreeT<NodeT>,
    triggerMeta: InputKey[] | undefined,
    fromEdit: { errorHandling: boolean; action_id: string },
    forEachContextId: string | null,
    triggerId?:string
): TreeT<NodeT> {
    if (child.children.length === 0) {
        if (child.value.kind == 'ForEach') {
            const collection =
                child.value.meta_data && child.value.meta_data.collection ? child.value.meta_data.collection : ''

            child.value = {
                ...child.value,
                // to handle error handling nodes context while updating parent action node
                input:
                    fromEdit.errorHandling && child.value.meta_data.action_id == fromEdit.action_id
                        ? child.value.input
                        : filterInput(child.value, nodeId, triggerMeta),
                meta_data:
                    child.value.meta_data && child.value.meta_data.collection.includes(nodeId)
                        ? {
                              ...child.value.meta_data,
                              collection: '',
                              collectionForRemoveContextVariable:
                                  child.value.meta_data && child.value.meta_data.collection
                                      ? child.value.meta_data.collection
                                      : ''
                          }
                        : { ...child.value.meta_data }
            }
            const subflow = (child.value.subflow as unknown) as { children: TreeT<NodeT>[] }

            child.value.subflow = {
                children: subflow.children.map(x =>
                    findContextVariableChilds(
                        nodeId,
                        x,
                        triggerMeta,
                        fromEdit,
                        collection.includes(nodeId) ? child.value.id : null
                    )
                )
            }

            return child
        }
        return {
            value: {
                ...child.value,
                description:
                    child.value.kind == 'Condition' ||
                    (child.value.kind == 'ErrorHandlingCondition' && fromEdit.errorHandling)
                        ? ''
                        : child.value.description,
                // to handle error handling nodes context while updating parent action node
                input:
                    fromEdit.errorHandling && child.value.meta_data.action_id == fromEdit.action_id
                        ? child.value.input
                        : forEachContextId
                        ? filterInput(child.value, forEachContextId, triggerMeta)
                        : filterInput(child.value, nodeId, triggerMeta, triggerId)
            },
            children: []
        }
    } else {
        const childs =
            fromEdit.errorHandling &&
            child.value.meta_data &&
            child.value.meta_data.action_id == fromEdit.action_id &&
            child.value.name.toLowerCase() == 'success'
                ? child.children
                      .map(chil => {
                          if (chil.value.kind == 'ErrorHandlingCondition') {
                              return { ...chil, value: { ...chil.value, description: '' } }
                          }

                          return chil
                      })
                      .map(x => findContextVariableChilds(nodeId, x, triggerMeta, fromEdit, forEachContextId, triggerId))
                : child.children.map(x => findContextVariableChilds(nodeId, x, triggerMeta, fromEdit, forEachContextId, triggerId))

        if (child.value.kind == 'ForEach') {
            const collection =
                child.value.meta_data && child.value.meta_data.collection ? child.value.meta_data.collection : ''

            child.value = {
                ...child.value,
                // to handle error handling nodes context while updating parent action node
                input:
                    fromEdit.errorHandling && child.value.meta_data.action_id == fromEdit.action_id
                        ? child.value.input
                        : filterInput(child.value, nodeId, triggerMeta),
                meta_data:
                    child.value.meta_data && child.value.meta_data.collection.includes(nodeId)
                        ? {
                              ...child.value.meta_data,
                              collection: '',
                              collectionForRemoveContextVariable:
                                  child.value.meta_data && child.value.meta_data.collection
                                      ? child.value.meta_data.collection
                                      : ''
                          }
                        : { ...child.value.meta_data }
            }
            const subflow = (child.value.subflow as unknown) as { children: TreeT<NodeT>[] }

            child.value.subflow = {
                children: subflow.children.map(x =>
                    findContextVariableChilds(
                        nodeId,
                        x,
                        triggerMeta,
                        fromEdit,
                        collection.includes(nodeId) ? child.value.id : null
                    )
                )
            }

            child.children = childs

            return child
        }

        return {
            value: {
                ...child.value,
                description:
                    child.value.kind == 'Condition' ||
                    (child.value.kind == 'ErrorHandlingCondition' && fromEdit.errorHandling)
                        ? ''
                        : child.value.description,
                // to handle error handling nodes context while updating parent action node
                input:
                    fromEdit.errorHandling &&
                    child.value.meta_data &&
                    child.value.meta_data.action_id == fromEdit.action_id
                        ? child.value.input
                        : forEachContextId
                        ? filterInput(child.value, forEachContextId, triggerMeta)
                        : filterInput(child.value, nodeId, triggerMeta,triggerId)
            },
            children: childs
        }
    }
}

export function removeValuesToMultipleInputs(
    inputs: InputFieldT[],
    nodeId: string,
    triggerMeta: InputKey[] | undefined,
    combiningResponseWithTriggerOutput?: ActionInputsListT[],
    triggerOutput?: NodeT,
    contextPopupValues?: ContextPopupStateT,
    setContextPopupValues?: any,
    donotRemoveCollection?: boolean
) {
    const x = inputs.map(val => {
        if (val.config.kind !== 'multiple') {
            const valueAlwaysString = typeof val.value != 'string' ? convertToServerFormat(val.value) : val.value

            if (val.config.kind == 'hidden' || configKinds.some(value => value === val.config.kind)) {
                return val
            } else if (
                val.value &&
                valueAlwaysString.length > 0 &&
                valueAlwaysString.includes(nodeId) &&
                (!donotRemoveCollection || (donotRemoveCollection && val.config.kind != 'collection-picker-single'))
            ) {
                const splittedValue = getValuesBetweenBraces(valueAlwaysString).filter(e => {
                    return (
                        (e.includes('.output.') || e.includes('.input.')) &&
                        e.includes(nodeId) &&
                        (!triggerMeta || (triggerMeta && triggerMeta.filter(x => e.includes(x.uniqueKey)).length === 0))
                    )
                })
                // const replacableValue = splittedValue.length > 0 ? '${' + splittedValue[0] + '}' : ''
                // const { value, ...rest } = { ...val, value: valueAlwaysString.replace(replacableValue, '') }

                const acc = splittedValue.reduce((acc: string, curr: string) => {
                    return acc.replace('${' + curr + '}', '')
                }, valueAlwaysString)

                const { value, ...rest } = { ...val, value: acc }

                if (typeof val.value == 'string') {
                    return value.length > 0 ? { ...rest, value } : { ...rest }
                } else {
                    const actionInputs = getInputsInNestedLevel(inputs as InputFieldT[]).filter(
                        input =>
                            input.config.kind == 'text' ||
                            input.config.kind == 'password' ||
                            input.config.kind == 'collection-mapper' ||
                            input.config.kind == 'collection-picker-single'
                    )
                    return value.length > 0
                        ? {
                              ...rest,
                              value: converToEditorFormat(
                                  value,
                                  combiningResponseWithTriggerOutput as ActionInputsListT[],
                                  triggerOutput as NodeT,
                                  val,
                                  {
                                      inputs: actionInputs,
                                      contextPopupValues: contextPopupValues as ContextPopupStateT,
                                      setContextPopupValues
                                  }
                              )
                          }
                        : { ...rest, value: emptyEditorState }
                }
            }
            return val
        } else {
            const items = Object.values(val.config.items) as InputFieldT[]

            return {
                ...val,
                config: {
                    ...val.config,
                    items: removeValuesToMultipleInputs(
                        items,
                        nodeId,
                        triggerMeta,
                        combiningResponseWithTriggerOutput,
                        triggerOutput,
                        contextPopupValues,
                        setContextPopupValues,
                        donotRemoveCollection
                    )
                }
            }
        }
    }) as any

    return x.reduce((acc: any, curr: any, i: any) => {
        acc[curr.key] = curr
        return acc
    }, {})
}

function filterInput(value: NodeT, nodeId: string, triggerMeta: InputKey[] | undefined, triggerId?:string) {
    switch (value.kind) {
        case 'Action':
        case 'Approval':
        case 'Function': {
            const input = value.input as InputFieldT[]
            return input.map(val => {
                const valueAlwaysString = val.value && (val.value as string)
                if (val.config.kind !== 'multiple') {
                    if (val.config.kind == 'hidden' || configKinds.some(value => value === val.config.kind)) {
                        return val
                    } else if (
                        val.value &&
                        typeof val.value == 'string' &&
                        valueAlwaysString.length > 0 &&
                        valueAlwaysString.includes(nodeId)
                    ) {
                        const splittedValue = getValuesBetweenBraces(valueAlwaysString).filter(e => {
                            return (
                                (e.includes('.output.') || e.includes('.input.')) &&
                                e.includes(nodeId) &&
                                (!triggerMeta ||
                                    (triggerMeta && triggerMeta.filter(x => e.includes(x.uniqueKey)).length === 0))
                            )
                        })
                        const replacableValue = splittedValue.reduce((acc, val) => {
                            return acc.replace('${' + val + '}', '')
                        }, valueAlwaysString)

                        const { value, ...rest } = { ...val, value: replacableValue }

                        return value.length > 0 ? { ...rest, value } : { ...rest }
                    } else if (val.value && typeof val.value == 'object') {
                        const obj = Object.entries(val.value)

                            .map(ob => {
                                if (ob[1] && ob[1].length > 0) {
                                    const splittedValue = getValuesBetweenBraces(ob[1]).filter(e => {
                                        return (
                                            (e.includes('.output.') || e.includes('.input.')) &&
                                            e.includes(nodeId) &&
                                            (!triggerMeta ||
                                                (triggerMeta &&
                                                    triggerMeta.filter(x => e.includes(x.uniqueKey)).length === 0))
                                        )
                                    })

                                    // const replacableValue =
                                    //     splittedValue.length > 0 ? '${' + splittedValue[0] + '}' : ''
                                    const replacableValue = splittedValue.reduce((acc, val) => {
                                        return acc.replace('${' + val + '}', '')
                                    }, ob[1])

                                    return {
                                        key: ob[0],
                                        value: replacableValue
                                    }
                                }

                                return { key: ob[0], value: '' }
                            })

                            .reduce((acc, val) => {
                                return { ...acc, [`${val.key}`]: val.value }
                            }, {})

                        const { value, ...rest } = {
                            ...val,
                            value: obj
                        }

                        return { ...rest, value }
                    }

                    return val
                } else {
                    const items = Object.values(val.config.items) as InputFieldT[]

                    return {
                        ...val,
                        config: {
                            ...val.config,
                            items: removeValuesToMultipleInputs(items, nodeId, triggerMeta)
                        }
                    }
                }
            }) as InputFieldT[]
        }
        case 'Condition':
        case 'ErrorHandlingCondition': {
            const input = value.input as InputRulesArray[]

            return input.map(val => {
                if (val.type !== 'combinator') {
                    let currentValue = val

                    if (val.left.includes(nodeId) && (!triggerId || !val.left.includes(triggerId)) ) {
                        const splittedValue = getValuesBetweenBraces(val.left).filter(e => {
                            return (
                                (e.includes('.output.') || e.includes('.input.')) &&
                                e.includes(nodeId) &&
                                (!triggerMeta ||
                                    (triggerMeta && triggerMeta.filter(x => e.includes(x.uniqueKey)).length === 0))
                            )
                        })
                        const replacableValue = splittedValue.length > 0 ? '${' + splittedValue[0] + '}' : ''

                        currentValue = {
                            left: val.left.replace(replacableValue, ''),
                            operator: '',
                            right: '',
                            valType: '',
                            type: 'operator'
                        }
                    } else if (val.right.includes(nodeId) && (!triggerId || !val.left.includes(triggerId))) {
                        const splittedValue = getValuesBetweenBraces(val.right).filter(e => {
                            return (
                                (e.includes('.output.') || e.includes('.input.')) &&
                                e.includes(nodeId) &&
                                (!triggerMeta ||
                                    (triggerMeta && triggerMeta.filter(x => e.includes(x.uniqueKey)).length === 0))
                            )
                        })
                        // const replacableValue = splittedValue.length > 0 ? '${' + splittedValue[0] + '}' : ''
                        const replacableValue = splittedValue.reduce((acc, val) => {
                            return acc.replace('${' + val + '}', '')
                        }, val.right)

                        currentValue = {
                            ...val,
                            right: replacableValue
                        }
                    }

                    return currentValue
                }

                return val
            })
        }

        default:
            return value.input
    }
}

export function deleteNode(
    deletingNode: NodeT,
    parentNode: NodeT,
    tree: TreeT<NodeT>,
    isErrorHandlingNode: boolean,
    addingInsideForEach: {
        addingInsideForEach: boolean
        forEachNodeId: string
        parentNode: NodeT
    }
) {
    let clonedTree = R.clone(tree)

    if (clonedTree.value.id === deletingNode.id) {
        if (clonedTree.children.length == 1) {
            const myChild = clonedTree.children[0].value
            const myChildren = clonedTree.children[0].children
            clonedTree.value = myChild
            clonedTree.children = myChildren
        }
    } else {
        if (
            clonedTree.value.id === parentNode.id ||
            (addingInsideForEach && addingInsideForEach.forEachNodeId == clonedTree.value.id)
        ) {
            if (!addingInsideForEach || !addingInsideForEach.addingInsideForEach) {
                const isDeleteNodeisParallelParent = clonedTree.children.filter(x => x.value.id === deletingNode.id)
                const deletingParallelpathSibling =
                    clonedTree.children.length > 1 ||
                    (isDeleteNodeisParallelParent.length > 0 && isDeleteNodeisParallelParent[0].children.length > 1)

                // handling error handling node delete
                if (isErrorHandlingNode) {
                    clonedTree.value = produce(clonedTree.value, draft => {
                        if (draft.meta_data) {
                            draft.meta_data.errors =
                                draft.meta_data && draft.meta_data.errors
                                    ? draft.meta_data.errors.map((error: { name: string; enabled: boolean }) => {
                                          return error.name.toLowerCase() == deletingNode.name.toLowerCase()
                                              ? { ...error, enabled: false }
                                              : error
                                      })
                                    : ActionInitialState.errorHandling.errors
                        } else {
                            draft = { ...draft, meta_data: { errors: ActionInitialState.errorHandling.errors } }
                        }
                    })
                }

                if (deletingParallelpathSibling) {
                    clonedTree.children = clonedTree.children.filter(x => x.value.id !== deletingNode.id)
                } else {
                    const clonedChildren = clonedTree.children.map(x => {
                        if (x.value.id === deletingNode.id && x.children.length > 0) {
                            const myChild = x.children[0].value
                            const myChildren = x.children[0].children
                            x.value = myChild
                            x.children = myChildren
                            return x
                        } else if (x.value.id === deletingNode.id) {
                            return undefined
                        }

                        return x
                    })

                    const filteredChilds = clonedChildren.filter(Boolean) as TreeT<NodeT>[]

                    clonedTree.children = filteredChilds.map(child => {
                        if (child.value.kind == 'ErrorHandlingCondition') {
                            return { ...child, value: { ...child.value, description: '' } }
                        }
                        return child
                    })
                }
            } else {
                const subflow = clonedTree.value.subflow as { children: TreeT<NodeT>[] }

                subflow.children = subflow.children
                    .map((x: TreeT<NodeT>) => {
                        if (
                            deletingNode.id == x.value.id &&
                            (x.children.length > 1 || x.children.length == 0 || x.value.kind == 'ParallelPath')
                        ) {
                            return null
                        }
                        return deleteNode(deletingNode, parentNode, x, isErrorHandlingNode, {
                            ...addingInsideForEach,
                            addingInsideForEach: false
                        })
                    })
                    .filter(Boolean) as any

                clonedTree.value = { ...tree.value, subflow: subflow }
            }
        } else {
            clonedTree.children = tree.children
                .map(x => {
                    if (deletingNode.id == x.value.id && (x.children.length > 1 || x.children.length == 0)) {
                        return null
                    }
                    return deleteNode(deletingNode, parentNode, x, isErrorHandlingNode, addingInsideForEach)
                })
                .filter(Boolean) as any
        }
    }
    return clonedTree
}

export function checkInputHasValue(value: NodeT) {
    function inputHasValue(currentValue: any) {
        // to check whether if any context has been selected for this input then enable save
        if (
            currentValue.config.kind == 'hidden' ||
            (currentValue.config.kind == 'switch' && currentValue.config.id == 'allow_numbering')
        ) {
            return true
        } else if (typeof currentValue.value == 'string') {
            return currentValue.value.length > 0
        }if (Array.isArray( currentValue.value)) {
            return currentValue.value.length > 0
        } else if (typeof currentValue.value == 'object') {
            return (
                Object.values(currentValue.value).filter((v: any) => v.length > 0).length ==
                Object.values(currentValue.value).length
            )
        }
        return (
            currentValue.value.getCurrentContent &&
            convertAsaSingleString(convertToRaw(currentValue.value.getCurrentContent()).blocks).length > 0
        )
    }

    switch (value.kind) {
        case 'Timer':
            return true
        case 'Action':
        case 'EventTrigger':
        case 'AppTrigger': {
            const input = value.input as InputFieldT[]
            const comparingInput = input.filter(
                (val: InputFieldT) => val.required && val.config.kind != 'hidden' && val.config.kind != 'switch'
            ) as InputFieldT[]

            const filteredInput = input.filter(
                val =>
                    val.required &&
                    val.value &&
                    val.config.kind != 'hidden' &&
                    val.config.kind != 'switch' &&
                    inputHasValue(val)
            )

            return filteredInput.length === comparingInput.length
        }
        case 'Function': {
            const input = getInputsInNestedLevel(value.input as InputFieldT[])

            const comparingInput = input.filter(
                (val: InputFieldT) => val.required && val.config.kind != 'hidden' && val.config.kind != 'switch'
            ) as InputFieldT[]

            const filteredInput = input.filter(
                val =>
                    val.required &&
                    val.value &&
                    val.config.kind != 'hidden' &&
                    val.config.kind != 'switch' &&
                    inputHasValue(val)
            )

            return filteredInput.length === comparingInput.length
        }
        case 'Approval': {
            const input = value.input as InputFieldT[]

            return checkNodeInputHasValue(input, 'inputs')
        }
        case 'Notification':{
            const input = value.input as InputFieldT[]
            return checkNodeInputHasValue((value as any).notification_input_type
             ? ((value as any).notification_input_type === 'adaptive_card'
             ? input.slice(0,2)
             : (value as any).notification_input_type==='option'
             ? input.slice(0,1)
             : input)
             : input, 'inputs')
        }
        case 'ForEach': {
            return value.meta_data && value.meta_data.collection && value.meta_data.collection.length > 0
        }
        case 'Condition':
        case 'ErrorHandlingCondition': {
            const input = value.input as InputRulesArray[]

            const comparingInput = input.filter(
                (val: InputRulesArray) => val.type !== 'combinator'
            ) as InputRulesArray[]

            const filteredInput = input.filter(
                val =>
                    val.left &&
                    val.left.length > 0 &&
                    (((val.valType && val.valType === 'boolean') || !isBinaryOperatorValue(val.operator))  || (val.right && val.right.length > 0))
            )

            return filteredInput.length === comparingInput.length
        }

        default:
            return false
    }
}

export enum Nodes {
    Trigger,
    NoTrigger,
    Webhook,
    Setup,
    Action,
    Condition,
    Function,
    Foreach,
    Approval,
    Timer,
    Notification,
    End
}

export type ActionT = {
    kind: Nodes.Action
    Component: any
    SelectApplication: any
    Inputs: any
    ErrorHandling: any
    ActionInfo: any
}

export type ConditionT = {
    kind: Nodes.Condition
    Component: any
    SelectApplication: any
    Inputs: any
    ErrorHandling: any
    ActionInfo: any
}

export type CanvasRightPanelT = {
    Component: any
    ToggleArrow: any
    AppsDropDown: any
    StepHeader: any
    Step: TriggerT | ActionT | ConditionT
}

export const Action: ActionT = {
    kind: Nodes.Action,
    Component: null,
    SelectApplication: null,
    Inputs: null,
    ErrorHandling: null,
    ActionInfo: null
}

export enum AuthType {
    Apitoken = 'api_token',
    Basic = 'basic',
    Oauth = 'oauth',
    Custom = 'custom',
    CustomOauth = 'custom_oauth',
    OnPrem = 'on_prem',
    None = '',
    NoAuth = 'none',
    PrivateOauth = "private_oauth"
}
export type AuthenticationType = 'api_token' | 'basic' | 'oauth' | ''

export type ResponseOutputT = {
    action: string
    appId: string
    appName: string
    createdDate: string
    file: string
    authentication_type: AuthType
    id: string
    scope: string
    icon: string
    type: 'trigger' | 'action' | 'function' | 'event_trigger'
    version: string
    latest: boolean
}

export const ResponseOutput: ResponseOutputT & PathIdType = {
    action: '',
    appId: '',
    appName: '',
    createdDate: '',
    file: '',
    authentication_type: AuthType.None,
    id: '',
    scope: '',
    icon: '',
    type: 'action',
    version: '',
    latest: false,
    pathId: {
        actionId: '',
        nodeId: ''
    }
}

export const emptyInputSchema: InputFieldT = {
    key: '',
    displayTitle: '',
    description: '',
    required: true,
    value: '',
    path: '',
    inputHasValue: false,
    disabled: false,
    config: { kind: 'hidden' }
}

export const MultipleValueInitialInputs: MultipleValueInputsT = {
    isCollectionPicker: false,
    selectedCollection: {
        name: '',
        value: {
            $id: '',
            type: 'collection',
            item: {
                type: 'object',
                properties: {}
            },
            title: '',
            properties: {}
        }
    },
    path: '',
    parentKey: '',
    collectionType: emptyInputSchema,
    app: ResponseOutput
}

export type FunctionResponseOutputT = {
    action: string
    appId: string
    appName: string
    createdDate: string
    file: string
    authentication_type: 'none'
    id: string
    scope: string
    icon: string
    type: 'function'
    version: string
    latest: boolean
    description?: string
}

export type AppsListT = {
    type: string
    output: ResponseOutputT[]
}

export type FunctionsListT = {
    type: string
    output: FunctionResponseOutputT[]
}

export type LookUpEnum = {
    kind: 'lookup-enum'
    lookup: string
    params: {
        [key: string]: string
    }
}

export type MultipleLookUpEnum = {
    kind: 'multiselect-lookup-enum'
    lookup: string
    params: {
        [key: string]: string
    }
}

export type OptionT = {
    kind:"option"
}

export type adaptive_card_buttonT={
    kind:"adaptive_card_button"
}

export type adaptive_card_fieldT={
    kind:"adaptive_card_field"
}

export type InputFile = {
    kind: 'file'
}

export type InputEnum = {
    kind: 'enum'
    values: {
        text: string
        value: any
    }[]
}

export type MultiSelectEnum = {
    kind: 'multiselect-enum'
    values: {
        text: string
        value: any
    }[]
}

export type InputDate = {
    kind: 'date'
}

export type CollectionPickerSingle = {
    kind: 'collection-picker-single'
}

export type CollectionPickerHidden = {
    kind: 'hidden'
}

export type CollectionMapper = {
    kind: 'collection-mapper'
}

export type Switch = {
    kind: 'switch'
}

export type InputText = {
    kind: 'text'
}

export type InputPassword = {
    kind: 'password'
}

export type DefaultInputKeysT = {
    key: string
    displayTitle: string
    description: string
    required: boolean
    value: string | EditorState
    path?: string
    inputHasValue?: boolean
    disabled?: boolean
}

export type ConfigKinds =
    | InputEnum
    | InputText
    | InputPassword
    | LookUpEnum
    | InputDate
    | MultipleT
    | CollectionPickerSingle
    | CollectionPickerHidden
    | Switch
    | CollectionMapper
    | InputFile
    | InputDictionary
    | MultiSelectEnum
    | MultipleLookUpEnum
    | OptionT
    | adaptive_card_buttonT
    | adaptive_card_fieldT

export type MultipleT = {
    kind: 'multiple'
    items: {
        [key: string]: DefaultInputKeysT & { order: number } & {
            config: ConfigKinds & ConfigIdT
        }
    }
}

export type ConfigIdT = { id?: string }

export type InputFieldT = DefaultInputKeysT & { config: ConfigKinds & ConfigIdT }

export type ConfigPropertiesTypeT = 'object'

export type OutpuTypeT = 'string' | 'boolean' | 'date' | 'collection' | 'file'

export type OutputListTypeT = {
    $id: string
    type: OutpuTypeT
    title: string
    examples: any[]
    value?: string
}

export type ObjectOutputListTypeT = {
    $id: string
    type: 'object'
    title: string
    properties: {
        [key: string]: OutputListTypeT
    }
}

export type CollectionItem = {
    type: 'object'
    properties: {
        [key: string]: OutputListTypeT
    }
}

export type CollectionOutputListTypeT = {
    $id: string
    type: 'collection'
    item: CollectionItem
    title: string
    properties: {
        [key: string]: OutputListTypeT
    }
    showForEachCollection?: boolean
    collection_response?: ObjectOutputListTypeT[]
}

export type PathIdType = {
    pathId: {
        actionId: string
        nodeId: string
        approvalId?: string
        forEachName?: string
        forEachId?: string | undefined
        forEachNodeId?: string | undefined
    }
}

export type ActionInputsListT = {
    type: string
    output: {
        name: string
        interval?: string
        input: InputFieldT[]
        app?: ResponseOutputT & PathIdType
        forEachCollection?: boolean
        output: {
            type: ConfigPropertiesTypeT
            title: string
            properties: {
                [key: string]: OutputListTypeT | ObjectOutputListTypeT | CollectionOutputListTypeT
            }
        }
    }
}

export type ConnectionOutputT = {
    authStoreMap: string
    name: string
}

export enum ConnectionTypeT {
    old,
    new
}

export type ApitokenMeta = {
    api_token: string
}

export type OauthtokenMeta = {
    authStoreMap: string
}

export type BasicMeta = {
    username: string
    password: string
}

export type OnPremMeta = {
    agent: string
    connection: string
}

export type CustomAuthT = {
    type: AuthType.Custom
    customInputs: {
        id: string
        inputs: InputFieldT[]
    }
    value: {
        name: string
        type: AuthType
        appId: string
        workspace: string
        meta_data: any
    }
}

export type CustomOAuthT = {
    type: AuthType.CustomOauth
    customInputs: {
        id: string
        inputs: InputFieldT[]
    }
    value: {
        name: string
        type: AuthType
        appId: string
        workspace: string
        meta_data: any
    }
}

export type ApitokenT = {
    type: AuthType.Apitoken
    value: {
        name: string
        type: AuthType
        appId: string
        workspace: string
        meta_data: ApitokenMeta
    }
}

export type OauthtokenT = {
    type: AuthType.Oauth
    value: {
        name: string
        type: AuthType
        appId: string
        workspace: string
        meta_data: OauthtokenMeta
    }
}

export type BasicT = {
    type: AuthType.Basic
    value: {
        name: string
        type: AuthType
        appId: string
        workspace: string
        meta_data: BasicMeta
    }
}

export type OnPremAuthT = {
    type: AuthType.OnPrem
    value: {
        name: string
        type: AuthType
        appId: string
        workspace: string
        meta_data: OnPremMeta
    }
    selectedAgent: string
    agents: onPremListResponseT[]
    connections: onPremConnectionListResponseT[]
}

export type NewConnectionTypeT = ApitokenT | BasicT | OauthtokenT | CustomAuthT | OnPremAuthT | CustomOAuthT

export type OldConnectionT = {
    type: ConnectionTypeT.old
    selectedConnection: string
    id: string
    valid: boolean | null
    hasError: string | null
    loading: boolean
    connectionsDetails: NewConnectionTypeT
}

export type NewConnectionT = {
    type: ConnectionTypeT.new
    id: string
    valid: boolean | null
    hasError: string | null
    loading: boolean
    selectedConnection: string
    connectionsDetails: NewConnectionTypeT
    error: { show: boolean; info: string }
    connectionEditing: boolean
}

export const ApiTokenValue: ApitokenT = {
    type: AuthType.Apitoken,
    value: {
        name: '',
        type: AuthType.Apitoken,
        appId: '',
        workspace: '',
        meta_data: {
            api_token: ''
        }
    }
}

export const OatuhTokenValue: OauthtokenT = {
    type: AuthType.Oauth,
    value: {
        name: '',
        type: AuthType.Oauth,
        appId: '',
        workspace: '',
        meta_data: {
            authStoreMap: ''
        }
    }
}

export const BasicValue: BasicT = {
    type: AuthType.Basic,
    value: {
        name: '',
        type: AuthType.Basic,
        appId: '',
        workspace: '',
        meta_data: {
            username: '',
            password: ''
        }
    }
}

export const CustomAuthValue: CustomAuthT = {
    type: AuthType.Custom,
    customInputs: { id: '', inputs: [] },
    value: {
        name: '',
        type: AuthType.Custom,
        appId: '',
        workspace: '',
        meta_data: {}
    }
}

export const CustomOAuthValue: CustomOAuthT = {
    type: AuthType.CustomOauth,
    customInputs: { id: '', inputs: [] },
    value: {
        name: '',
        type: AuthType.CustomOauth,
        appId: '',
        workspace: '',
        meta_data: {}
    }
}

export const OnpremAuthValue: OnPremAuthT = {
    type: AuthType.OnPrem,
    agents: [],
    connections: [],
    selectedAgent: 'Select',
    value: {
        name: '',
        type: AuthType.OnPrem,
        appId: '',
        workspace: '',
        meta_data: {
            agent: '',
            connection: ''
        }
    }
}

export const NewConnection: NewConnectionT = {
    type: ConnectionTypeT.new,
    id: '',
    hasError: null,
    valid: null,
    loading: false,
    selectedConnection: 'Add a new Account',
    connectionsDetails: ApiTokenValue,
    error: { show: false, info: '' },
    connectionEditing: false
}

export const oldConnection: OldConnectionT = {
    type: ConnectionTypeT.old,
    id: '',
    valid: null,
    hasError: null,
    loading: false,
    selectedConnection: '',
    connectionsDetails: ApiTokenValue
}

export type SelectedConnectionT = OldConnectionT | NewConnectionT

export type ActionConnectionsT = {
    type: string
    output: ConnectionOutputT[]
}

export type SubflowMeta =
    | { subflow: { hasSubFlow: false } }
    | {
          subflow: {
              hasSubFlow: true
              forEachNodeId: string
              parentId: string
          }
      }

export type ActionsResponseT = {
    nodeType: Nodes.Action
    appsList: AppsListT
    selectedApp: { name: string; id: string; type: AuthType; icon: string; version: string }
    selectedAction: { name: string; id: string; description: string }
    selectedConnection: SelectedConnectionT
    actionInputsList: ActionInputsListT
    workflowInputsListForTest: ActionInputsListT
    connections: ActionConnectionsT
    errorHandling: {
        errorHandling: boolean
        errorHandlingParent: NodeT
        errors: { name: string; enabled: boolean }[]
        errorHandlingChildrenForAddMode: TreeT<NodeT>[]
    }
    parallelPathNode: {
        currentParallelPathNode: NodeT | null
        anotherEmptyChildren: NodeT | null
        emptyChildrenPosition: number
        replacingParallelSibling: NodeT | null
    }
    addingInsideForEach: boolean
    subflowMetaData: SubflowMeta
    actionId:string
}

export type hasCombinatorT = {
    type: string
    combinator: string
}

export type RulesArray = {
    left: string
    operator: string
    right: EditorState | string
    rightHasValue: boolean
    valType: string
    type: string
    hasCombinator: null | hasCombinatorT
}

export type InputRulesArray = {
    left: string
    operator: string
    right: string
    valType: string
    type: string
}

export type ConditionResponseT = {
    nodeType: Nodes.Condition
    appsList: AppsListT
    conditionName: string
    description: string
    rules: RulesArray[]
    input: (InputRulesArray | hasCombinatorT)[]
    parallelPathNode: {
        currentParallelPathNode: NodeT | null
        anotherEmptyChildren: NodeT | null
        emptyChildrenPosition: number
        replacingParallelSibling: NodeT | null
    }
    addingInsideForEach: boolean
    subflowMetaData: SubflowMeta
}

export type ApprovalResponseT = {
    nodeType: Nodes.Approval
    appsList: AppsListT
    name: string
    description: string
    input: InputFieldT[]
    parallelPathNode: {
        currentParallelPathNode: NodeT | null
        anotherEmptyChildren: NodeT | null
        emptyChildrenPosition: number
        replacingParallelSibling: NodeT | null
    }
    addingInsideForEach: boolean
    subflowMetaData: SubflowMeta
}

export type TriggersResponseT = {
    nodeType: Nodes.Trigger
}

export type FunctionResponseT = {
    nodeType: Nodes.Function
    functionsList: FunctionsListT
    selectedFunction: {
        name: string
        id: string
        version: string
        function_id: string
        appName: string
        description: string
    }
    functionInputsList: ActionInputsListT
    parallelPathNode: {
        currentParallelPathNode: NodeT | null
        anotherEmptyChildren: NodeT | null
        emptyChildrenPosition: number
        replacingParallelSibling: NodeT | null
    }
    addingInsideForEach: boolean
    subflowMetaData: SubflowMeta
    combiningResponseWithTriggerOutput: ActionInputsListT[]
}
export type ForEachResponseT = {
    nodeType: Nodes.Foreach
    name: string
    description: string
    functionsList: FunctionsListT
    selectedCollection: string
    parallelPathNode: {
        currentParallelPathNode: NodeT | null
        anotherEmptyChildren: NodeT | null
        emptyChildrenPosition: number
        replacingParallelSibling: NodeT | null
    }
}

export type ChatChannelT = {
    channel: string;
    id: string;
    value: {
        icon: string;
    }[];
}

export type MessageType = {
    type: string;
    id: string;
}

export type NotificationResponseT = {
    nodeType: Nodes.Notification
    appsList: AppsListT
    notification_type:MessageType | null
    selectedBot: AssistantT | null
    message_type: MessageType | null
    user_type: MessageType | null
    selectedChatChannel:ChatChannelT | null
    selectedWorkspaceChannel: TeamsWorkspaceT| WorkspaceT | null
    actionInputsList: {output:{input:InputFieldT[]}}
    notification_title:EditorState|string
    user:EditorState|string
    options:dictionaryFieldT[]
    adaptivecard:{
        title:string
        description: string|EditorState
        url:string
        fields: dictionaryFieldT[]
        buttons: dictionaryFieldT[]
    }
    parallelPathNode: {
        currentParallelPathNode: NodeT | null
        anotherEmptyChildren: NodeT | null
        emptyChildrenPosition: number
        replacingParallelSibling: NodeT | null
    }
    addingInsideForEach: boolean
    subflowMetaData: SubflowMeta
}
export enum Mode {
    View,
    Add,
    Edit,
    Delete,
    Search,
    Select,
    GroupView,
    Loading,
    Error,
    MoveToGroup,
    AciveOrDeactive,
    EditAutomationMeta,
    DeleteFlow,
    NoDataFound
}

export enum NonCanvasActionModes {
    None,
    Loading,
    NotFound,
    Error,
    Deploy,
    SaveAs,
    EditAutomationMeta,
    UploadToMarketPlace,
    DownloadFromMarket,
    DeleteConnection,
    Acknowledge,
    Activate,
    Deactivate
}

export type NonCanvasActions =
    | NoneT
    | LoadingT
    | NotFoundT
    | EditAutomationMetaT
    | ErrorT
    | DeployT
    | SaveAsT
    | AcknowledgeT
    | UploadToMarketPlaceT
    | DownloadFromMarketT
    | DeleteConnectionT
    | ActivateWorkflowT
    | DeactivateWorkflowT

export type DeleteConnectionT = {
    mode: NonCanvasActionModes.DeleteConnection
    id: string
    type: string
    error: {
        error: boolean
        info: string
    }
}
export const Loading: LoadingT = {
    mode: NonCanvasActionModes.Loading
}
export const DeleteConnection: DeleteConnectionT = {
    mode: NonCanvasActionModes.DeleteConnection,
    id: '',
    type: '',
    error: {
        error: false,
        info: ''
    }
}

export const UploadToMarketPlace: UploadToMarketPlaceT = {
    mode: NonCanvasActionModes.UploadToMarketPlace,
    name: '',
    id: ''
}

export const DownloadFromMarketplace: DownloadFromMarketT = {
    mode: NonCanvasActionModes.DownloadFromMarket,
    id: '',
    error: {
        error: false,
        info: ''
    }
}

export const DeployAcknowledgement: AcknowledgeT = {
    mode: NonCanvasActionModes.Acknowledge,
    message: '<b>Automation deployed</b>'
}
export const UploadToMarketPlaceAcknowledgement: AcknowledgeT = {
    mode: NonCanvasActionModes.Acknowledge,
    message: '<b>Automation uploaded to Marketplace</b>'
}
export const SaveAs: SaveAsT = {
    mode: NonCanvasActionModes.SaveAs,
    name: '',
    description: '',
    error: {
        error: false,
        info: ''
    }
}

export const ErrorPage: ErrorT = {
    mode: NonCanvasActionModes.Error
}

export const NotFoundPage: NotFoundT = {
    mode: NonCanvasActionModes.NotFound
}

export const Deploy: DeployT = {
    mode: NonCanvasActionModes.Deploy,
    error: {
        error: false,
        info: ''
    }
}
export const ActivateAutomation: ActivateWorkflowT = {
    mode: NonCanvasActionModes.Activate,
    loading: true,
    error: {
        error: false,
        info: ''
    }
}

export const DeActivateAutomation: DeactivateWorkflowT = {
    mode: NonCanvasActionModes.Deactivate,
    loading: true,
    error: {
        error: false,
        info: ''
    }
}

export const None: NoneT = {
    mode: NonCanvasActionModes.None
}
export const EditAutomationMeta: EditAutomationMetaT = {
    mode: NonCanvasActionModes.EditAutomationMeta,
    name: '',
    description: '',
    error: {
        error: false,
        info: ''
    }
}

export type NoneT = {
    mode: NonCanvasActionModes.None
}

export type ErrorT = {
    mode: NonCanvasActionModes.Error
}
export type DownloadFromMarketT = {
    mode: NonCanvasActionModes.DownloadFromMarket
    id: string
    error: {
        error: boolean
        info: string
    }
}

export type LoadingT = {
    mode: NonCanvasActionModes.Loading
}

export type NotFoundT = {
    mode: NonCanvasActionModes.NotFound
}

export type UploadToMarketPlaceT = {
    mode: NonCanvasActionModes.UploadToMarketPlace
    id: string
    name: string
}

export type AcknowledgeT = {
    mode: NonCanvasActionModes.Acknowledge
    message: string
}

export type ActivateWorkflowT = {
    mode: NonCanvasActionModes.Activate
    loading: boolean
    error: {
        error: boolean
        info: string
    }
}
export type DeactivateWorkflowT = {
    mode: NonCanvasActionModes.Deactivate
    loading: boolean
    error: {
        error: boolean
        info: string
    }
}

export type DeployT = {
    mode: NonCanvasActionModes.Deploy
    error: {
        error: boolean
        info: string
    }
}

export type SaveAsT = {
    mode: NonCanvasActionModes.SaveAs
    name: string
    description: string
    error: {
        error: boolean
        info: string
    }
}

export type EditAutomationMetaT = {
    mode: NonCanvasActionModes.EditAutomationMeta
    name: string
    description: string
    error: {
        error: boolean
        info: string
    }
}

export const ResponseType: string = 'success'

export const ActionInitialState: ActionsResponseT = {
    nodeType: Nodes.Action,
    appsList: {
        type: ResponseType,
        output: []
    },
    selectedApp: { name: 'Select', id: '', type: AuthType.None, icon: '', version: '' },
    selectedAction: { name: 'Select', id: '', description: '' },
    selectedConnection: NewConnection,
    actionInputsList: {
        type: ResponseType,
        output: {
            name: '',
            input: [],
            output: {
                type: 'object',
                title: '',
                properties: {
                    sample: {
                        $id: '',
                        title: '',
                        type: 'string',
                        examples: []
                    }
                }
            }
        }
    },
    workflowInputsListForTest: {
        type: ResponseType,
        output: {
            name: '',
            input: [],
            output: {
                type: 'object',
                title: '',
                properties: {
                    sample: {
                        $id: '',
                        title: '',
                        type: 'string',
                        examples: []
                    }
                }
            }
        }
    },
    connections: {
        type: ResponseType,
        output: []
    },
    parallelPathNode: {
        currentParallelPathNode: null,
        anotherEmptyChildren: null,
        emptyChildrenPosition: 0,
        replacingParallelSibling: null
    },
    errorHandling: {
        errorHandling: false,
        errorHandlingParent: Tree,
        errors: [
            { name: 'success', enabled: false },
            { name: 'failed', enabled: false }
        ],
        errorHandlingChildrenForAddMode: []
    },
    addingInsideForEach: false,
    subflowMetaData: { subflow: { hasSubFlow: false } },
    actionId:""
}

export const ConditionInitialState: ConditionResponseT = {
    nodeType: Nodes.Condition,
    appsList: {
        type: ResponseType,
        output: []
    },
    rules: [
        {
            left: '',
            operator: 'Select',
            right: emptyEditorState,
            rightHasValue: false,
            valType: '',
            type: 'operator',
            hasCombinator: null
        }
    ],
    input: [],
    conditionName: '',
    description: '',
    parallelPathNode: {
        currentParallelPathNode: null,
        anotherEmptyChildren: null,
        emptyChildrenPosition: 0,
        replacingParallelSibling: null
    },
    addingInsideForEach: false,
    subflowMetaData: { subflow: { hasSubFlow: false } }
}

export const RemindEvery = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

export const ApprovalInitialState: ApprovalResponseT = {
    nodeType: Nodes.Approval,
    appsList: {
        type: ResponseType,
        output: []
    },

    input: [
        {
            config: { kind: 'text' },
            description: '',
            displayTitle: 'Requester name',
            inputHasValue: true,
            key: 'requester_name',
            required: true,
            value: emptyEditorState
        },
        {
            config: { kind: 'text' },
            description: '',
            displayTitle: 'Requester email',
            inputHasValue: true,
            key: 'requester_email',
            required: true,
            value: emptyEditorState
        },
        {
            config: { kind: 'text' },
            description: '',
            displayTitle: 'Assigned to',
            inputHasValue: true,
            key: 'assigned_to',
            required: true,
            value: emptyEditorState
        },
        {
            config: { kind: 'text' },
            description: '',
            displayTitle: 'Approval title',
            inputHasValue: true,
            key: 'approval_title',
            required: true,
            value: emptyEditorState
        },
        {
            config: { kind: 'text' },
            description: '',
            displayTitle: 'Approval description',
            inputHasValue: true,
            key: 'approval_details',
            required: true,
            value: emptyEditorState
        },
        {
            config: { kind: 'text' },
            description: '',
            displayTitle: 'Approval hyperlink:',
            inputHasValue: true,
            key: 'approval_details_link',
            required: false,
            value: emptyEditorState
        },
        {
            config: { kind: 'text' },
            description: '',
            displayTitle: 'Approval hyperlink text',
            inputHasValue: true,
            key: 'approval_link_description',
            required: false,
            value: emptyEditorState
        },
        {
            config: {
                kind: 'enum',
                values: [
                    {
                        text: 'Day',
                        value: 'day'
                    },
                    {
                        text: 'Hour',
                        value: 'hour'
                    },
                    {
                        text: 'Minute',
                        value: 'minute'
                    }
                ]
            },
            description: '',
            displayTitle: 'Approval Reminder',
            key: 'approval_reminder',
            required: false,
            value: 'day'
        },
        {
            config: {
                kind: 'enum',
                values: []
            },
            description: '',
            displayTitle: 'Trigger Every',
            key: 'trigger_every',
            required: false,
            value: 1 as any
        },
        {
            config: {
                kind: 'text'
            },
            description: '',
            displayTitle: 'Approval Expiry',
            key: 'approval_expiry',
            required: true,
            value: ''
        }
    ],
    name: '',
    description: '',
    parallelPathNode: {
        currentParallelPathNode: null,
        anotherEmptyChildren: null,
        emptyChildrenPosition: 0,
        replacingParallelSibling: null
    },
    addingInsideForEach: false,
    subflowMetaData: { subflow: { hasSubFlow: false } }
}

export const NotificationInitialState: NotificationResponseT = {
    nodeType: Nodes.Notification,
    appsList: {
        type: ResponseType,
        output: []
    },
    notification_title:emptyEditorState,
    actionInputsList:{
        output:{
        input: [
            {
                config: { kind: 'text' },
                description: '',
                displayTitle: 'Message to user',
                inputHasValue: true,
                key: 'message',
                required: true,
                value: emptyEditorState
            }
        ]
    }},
    options:[{ key:"" , value : emptyEditorState}],
    adaptivecard:{
        description:emptyEditorState,
        title:"",
        url:"",
        fields: [{ key:"" , value : emptyEditorState}],
        buttons: [{ key:"" , value : emptyEditorState}]
    },
    message_type:null,
    notification_type:null,
    user:emptyEditorState,
    selectedBot:null,
    selectedChatChannel:null,
    user_type:null,
    selectedWorkspaceChannel:null,
    parallelPathNode: {
        currentParallelPathNode: null,
        anotherEmptyChildren: null,
        emptyChildrenPosition: 0,
        replacingParallelSibling: null
    },
    addingInsideForEach: false,
    subflowMetaData: { subflow: { hasSubFlow: false } }
}

export const FunctionInitialState: FunctionResponseT = {
    nodeType: Nodes.Function,
    functionsList: {
        type: ResponseType,
        output: []
    },
    selectedFunction: { name: 'Select', id: '', version: '', function_id: '', appName: 'Select', description: '' },
    functionInputsList: {
        type: ResponseType,
        output: {
            name: '',
            input: [],
            output: {
                type: 'object',
                title: '',
                properties: {
                    sample: {
                        $id: '',
                        title: '',
                        type: 'string',
                        examples: []
                    }
                }
            }
        }
    },
    parallelPathNode: {
        currentParallelPathNode: null,
        anotherEmptyChildren: null,
        emptyChildrenPosition: 0,
        replacingParallelSibling: null
    },
    addingInsideForEach: false,
    subflowMetaData: { subflow: { hasSubFlow: false } },
    combiningResponseWithTriggerOutput: []
}

export const ForEachInitialState: ForEachResponseT = {
    nodeType: Nodes.Foreach,
    name: '',
    description: '',
    functionsList: {
        type: ResponseType,
        output: []
    },
    selectedCollection: '',
    parallelPathNode: {
        currentParallelPathNode: null,
        anotherEmptyChildren: null,
        emptyChildrenPosition: 0,
        replacingParallelSibling: null
    }
}

export const LeftSidePanelDefaultValue = {
    show: true,
    step1: {
        done: false
    },
    step2: {
        done: false
    },
    step3: {
        done: false
    },
    mode: NodeView,
    parentNode: { value: Tree, children: [] },
    currentNode: ActionInitialState
}
// export type TreeNode<T> = Array<TreeT<T>>

export const DefaultSelectedValue: { name: string; value: any[] } = {
    name: 'Select',
    value: []
}


export type ConditionOperator = { name: string; value: string ,type : "binary" | "unary" }
export const DefaultConditionValues: { [key: string]: { values: ConditionOperator[] } } = {
    number: {
        values: [
            {
                name: 'Equals',
                value: 'equals',
                type : "binary"
            },
            {
                name: 'Does not equal',
                value: 'not_equals',
                type : "binary"
            },
            {
                name: 'Greater than',
                value: 'greater_than',
                type : "binary"
            },
            {
                name: 'Less than',
                value: 'lesser_than',
                type : "binary"
            }
        ]
    },
    string: {
        values: [
            {
                name: 'Contains',
                value: 'contains',
                type : "binary"
            },
            {
                name: 'Doesn’t contain',
                value: 'not_contains',
                type : "binary"
            },
            {
                name: 'Starts with',
                value: 'starts_with',
                type : "binary"
            },
            {
                name: 'Doesn’t start with',
                value: 'not_starts_with',
                type : "binary"
            },
            {
                name: 'Ends with',
                value: 'ends_with',
                type : "binary"
            },
            {
                name: 'Is Empty',
                value: 'is_empty',
                type : "unary"
            },
            {
                name: 'Is Not Empty',
                value: 'is_not_empty',
                type : "unary"
            }
        ]
    },
    boolean: {
        values: [
            {
                name: 'Is true',
                value: 'is_true',
                type : "unary"
            },
            {
                name: 'Is false',
                value: 'is_false',
                type : "unary"
            }
        ]
    }
}

export const DefaultSelectedConnection: { name: string; value: any[] } = {
    name: 'Add a new Account',
    value: []
}

export const StarttIcon =
    'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDIzLjAuNCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkNhcGFfMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgeD0iMHB4IiB5PSIwcHgiCgkgdmlld0JveD0iMCAwIDE2IDE2IiBzdHlsZT0iZW5hYmxlLWJhY2tncm91bmQ6bmV3IDAgMCAxNiAxNjsiIHhtbDpzcGFjZT0icHJlc2VydmUiPgo8c3R5bGUgdHlwZT0idGV4dC9jc3MiPgoJLnN0MHtmaWxsOiMyREI2Nzc7fQo8L3N0eWxlPgo8cGF0aCBjbGFzcz0ic3QwIiBkPSJNMTMuODcsNi44MkwzLjQ1LDAuOEMyLjU3LDAuMywxLjQ4LDAuOTMsMS40OCwxLjkzdjEyLjAzYzAsMS4wMSwxLjA5LDEuNjQsMS45NywxLjE0bDEwLjQyLTYuMDIKCUMxNC43NCw4LjU4LDE0Ljc0LDcuMzIsMTMuODcsNi44MnoiLz4KPC9zdmc+Cg=='

export const DefaultTree: TreeT<NodeT> = {
    value: getProductId() === 'ASSISTANT'? {
        ...Tree,
        id: generateUniqueId(),
        name: 'Triggered by Chatbot',
        kind: 'NoTrigger',
        output: { properties: {} },
        icon: NoTriggerIcon,
        meta_data:{}
       } : {
        ...Tree,
        id: generateUniqueId(),
        name: 'Triggered by Application',
        kind: 'EventTrigger',
        output: { properties: {} },
        icon: ApptriggerIconNew,
        // meta_data:{}
       },
    children: [
        {
            value: {
                ...Tree,
                id: generateUniqueId(),
                name: 'Step 2 - Setup',
                kind: 'Setup',
                icon:
                    'data:image/svg+xml;base64,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'
            },
            children: []
        },
        {
            value: {
                ...Tree,
                id: generateUniqueId(),
                name: 'Step 3 - End',
                kind: 'End',
                meta_data: {
                    successEmail: '',
                    failureEmail: ''
                },
                icon:
                    'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDIzLjAuNCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkNhcGFfMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgeD0iMHB4IiB5PSIwcHgiCgkgdmlld0JveD0iMCAwIDE2IDE2IiBzdHlsZT0iZW5hYmxlLWJhY2tncm91bmQ6bmV3IDAgMCAxNiAxNjsiIHhtbDpzcGFjZT0icHJlc2VydmUiPgo8c3R5bGUgdHlwZT0idGV4dC9jc3MiPgoJLnN0MHtmaWxsOiNFNTRCMUE7fQo8L3N0eWxlPgo8cGF0aCBjbGFzcz0ic3QwIiBkPSJNMTIuODUsMTQuOEgzLjIxYy0xLjE0LDAtMi4wNi0wLjkyLTIuMDYtMi4wNlYzLjFjMC0xLjE0LDAuOTItMi4wNiwyLjA2LTIuMDZoOS42NGMxLjE0LDAsMi4wNiwwLjkyLDIuMDYsMi4wNgoJdjkuNjRDMTQuOTEsMTMuODgsMTMuOTksMTQuOCwxMi44NSwxNC44eiIvPgo8L3N2Zz4K'
            },
            children: []
        }
    ]
}

// Trigger
export type KeyDataType = 'Number' | 'Boolean' | 'String' | 'Date' | 'File'

export type InputKey = {
    title: string
    uniqueKey: string
    type: KeyDataType
    masked?: boolean
}
export type TriggerT = {
    triggerMeta: AppTriggerT | NoTriggerT | WebHookT
    outputSchema?: ObjectOutputListTypeT
}
export type AppTriggerT = {
    triggerType: 'AppTrigger'
}
export type NoTriggerT = {
    triggerType: 'NoTrigger'
    inputKeys: InputKey[]
}
export type WebHookT = {
    triggerType: "Webhook";
    inputKeys: (InputKey & { optional: boolean })[];
};

export type WebhookTypeT = "GET" | "POST";

export enum WebhookAuthType {
  None = "None",
  Basic = "Basic",
}

export type WebHookResponseT = {
  nodeType: Nodes.Webhook;
  name: string;
  webhookType: WebhookTypeT;
  authentication:
    | { auth: WebhookAuthType.None }
    | {
        auth: WebhookAuthType.Basic;
        username: string;
        password: string;
        authId: string;
      };
  trigger: TriggerT;
};

export type TriggerResponseT = {
    nodeType: Nodes.Trigger
    trigger: TriggerT
}

export type AppTriggerResponseT = {
    nodeType: Nodes.Trigger
    trigger: TriggerT
    appsList: AppsListT
    selectedApp: { name: string; id: string; type: AuthType; icon: string; version: string }
    selectedAction: { name: string; id: string; description: string; type: 'trigger' | 'event_trigger' }
    interval?: string
    selectedConnection: SelectedConnectionT
    actionInputsList: ActionInputsListT
    connections: ActionConnectionsT
    selectedPollingTime: number
    errorHandling: {
        errorHandling: boolean
        errorHandlingParent: NodeT
        errors: { name: string; enabled: boolean }[]
        errorHandlingChildrenForAddMode: TreeT<NodeT>[]
    }
    subflowMetaData: SubflowMeta
}

export type TimerResponseT = {
    nodeType: Nodes.Timer;
    appsList:AppsListT
    timerType: string;
    timerValue: string;
    waitFor: string;
    conditionTimeType: string;
    hour: number;
    timezone: string;
    currentDay:number;
    addingInsideForEach:boolean
    subflowMetaData: SubflowMeta
    dateVariable: ContextValueTypeT[]
    parallelPathNode: {
        currentParallelPathNode: NodeT | null
        anotherEmptyChildren: NodeT | null
        emptyChildrenPosition: number
        replacingParallelSibling: NodeT | null
    }
}

export const NoTriggerInitialState: TriggerResponseT = {
    nodeType: Nodes.Trigger,
    trigger: {
        triggerMeta: {
            triggerType: 'NoTrigger',
            inputKeys: []
        },
        outputSchema: undefined
    }
}

export const WebhookInitialState: WebHookResponseT = {
    nodeType: Nodes.Webhook,
    authentication: { auth: WebhookAuthType.None },
    trigger: {
      triggerMeta: {
        triggerType: "Webhook",
        inputKeys: [],
      },
      outputSchema: undefined,
    },
    name: "",
    webhookType: "POST",
};

export const AppTriggerInitialState: AppTriggerResponseT = {
    nodeType: Nodes.Trigger,
    trigger: {
        triggerMeta: {
            triggerType: 'AppTrigger'
        }
    },
    appsList: {
        type: ResponseType,
        output: []
    },
    selectedApp: { name: 'Select', id: '', type: AuthType.None, icon: '', version: '' },
    selectedAction: { name: 'Select', id: '', description: '', type: 'event_trigger' },
    selectedConnection: NewConnection,
    selectedPollingTime:0,
    actionInputsList: {
        type: ResponseType,
        output: {
            name: '',
            input: [],
            output: {
                type: 'object',
                title: '',
                properties: {
                    sample: {
                        $id: '',
                        title: '',
                        type: 'string',
                        examples: []
                    }
                }
            }
        }
    },
    connections: {
        type: ResponseType,
        output: []
    },
    errorHandling: {
        errorHandling: false,
        errorHandlingParent: Tree,
        errors: [
            { name: 'success', enabled: false },
            { name: 'failed', enabled: false }
        ],
        errorHandlingChildrenForAddMode: []
    },
    subflowMetaData: { subflow: { hasSubFlow: false } }
}

export const TimerInitialState: TimerResponseT = {
    nodeType: Nodes.Timer,
    timerType: 'wait_time',
    timerValue: 'day',
    waitFor: '',
    conditionTimeType: 'current_time',
    hour: 0,
    timezone: 'IST(UTC +5:30)',
    currentDay: 1,
    addingInsideForEach:false,
    subflowMetaData: { subflow: { hasSubFlow: false } },
    parallelPathNode: {
        currentParallelPathNode: null,
        anotherEmptyChildren: null,
        emptyChildrenPosition: 0,
        replacingParallelSibling: null
    },
    appsList:{
        type: ResponseType,
        output: []
    },
    dateVariable:[]
}

// End Node
export type EndResponseT = {
    nodeType: Nodes.End
    reportMeta: {
        successEmail: {
            valid: boolean
            value: string
        }
        failureEmail: {
            valid: boolean
            value: string
        }
    }
}

export const EndResponseInitialState: EndResponseT = {
    nodeType: Nodes.End,
    reportMeta: {
        successEmail: {
            valid: false,
            value: ''
        },
        failureEmail: {
            valid: false,
            value: ''
        }
    }
}

// run time logs types

export type outputConfig = {
    type: string
    output: {
        [key: string]: {
            $id: string
            type: string
            title: string
            value?: string
        }
    }
}

export type ActionNodeMeta = {
    action: {
        id: string
        auth_type: AuthType
        icon: string
        name: string
        description: string
    }
    authentication: AuthenticationT
}

export type ConditionNodeMeta = {
    name: string
}

export enum NodeTypeT {
    action = 'action',
    web_hook = 'web_hook',
    webhook_trigger = 'webhook_trigger',
    end = 'end',
    polling = 'polling',
    condition = 'condition',
    loop = 'loop',
    event_trigger = 'event_trigger',
    approval = 'approval',
    timer = 'timer',
    notification = 'notification'
}

export type executionStatus = 'failed' | 'success' | 'skipped'

export type PollingNodeLogT = {
    id: number
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.polling
    node_execution_status: executionStatus | "in_progress"
    input_schema: {
        [key: string]: string
    }
    output_schema: any
    node_meta: ActionNodeMeta
    inserted_date: string
    index: string
    order: number
}

export type EventTriggerNodeLogT = {
    id: number
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.event_trigger
    node_execution_status: executionStatus
    input_schema: {}
    output_schema: any
    node_meta: ActionNodeMeta
    inserted_date: string
    index: string
    order: number
}

export type WebHookNodeLogT = {
    id: number
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.web_hook
    node_execution_status: executionStatus
    input_schema: any
    output_schema: any
    node_meta: null
    inserted_date: string
    index: string
    order: number
}

export type WebHookTriggerNodeLogT = {
    id: number
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.webhook_trigger
    node_execution_status: executionStatus
    input_schema: any
    output_schema: any
    node_meta: null
    inserted_date: string
    index: string
    order: number
}

export type EndNodeLogT = {
    id: number
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.end
    node_execution_status: executionStatus
    input_schema: any
    output_schema: any
    node_meta: {
        failureEmail: string
        successEmail: string
    }
    inserted_date: string
    index: string
    order: number
}

export type AuthenticationT =
    | {
          api_token: string
          name: string
      }
    | {
          access_toke: string
          name: string
      }
    | {
          username: string
          password: string
          name: string
      }

export type ActionNodeLogT = {
    id: number
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.action
    node_execution_status: executionStatus
    input_schema: {
        [key: string]: string
    }
    output_schema: any
    node_meta: ActionNodeMeta
    inserted_date: string
    index: string
    order: number
}

export type ApprovalNodeLogT = {
    id: number
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.approval
    node_execution_status: executionStatus | "in_progress"
    input_schema: InputSchema[]
    output_schema: any
    node_meta: {
        pathId: string
    }
    inserted_date: string
    index: string
    order: number
}

export type TimerNodeLogT = {
    id: number
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.timer
    node_execution_status: executionStatus | "in_progress"
    input_schema: {[key:string]:string|number}
    output_schema: any
    node_meta: {
        pathId: string
    }
    inserted_date: string
    index: string
    order: number
}

export type NotificationNodeLogT = {
    id: number
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.notification
    node_execution_status: executionStatus
    input_schema: InputSchema[]
    output_schema: any
    node_meta: {
        pathId: string
    }
    inserted_date: string
    index: string
    order: number
}

export type ConditionNodeLogT = {
    id: number
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.condition
    node_execution_status: executionStatus
    input_schema: {
        [key: string]: string
    }
    output_schema: any
    node_meta: ConditionNodeMeta
    inserted_date: string
    index: string
    order: number
}

export type NormalViewActionNodeLogT = {
    id: number
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.action
    node_execution_status: executionStatus
    input_schema: InputSchema[]
    output_schema: any
    node_meta: ActionNodeMeta
    inserted_date: string
    index: string
    order: number
}

export type ForEachNodeLogT = {
    execution_id: string
    automation_id: string
    node_id: string
    node_type: NodeTypeT.loop
    node_execution_status: executionStatus
    jobs: (NormalJobDataT | DeveloperJobDataT)[]
    index: string
    order: number
    node_meta: {
        loop: {
            name: string
            node_id: string
        }
    }
}

export type NormalJobDataT = {
    jobId: string
    id: number
    node_execution_status: executionStatus
    input_schema: InputSchema[]
    output_schema: any
    node_meta: { job: number; loop: string }
    inserted_date: string
    actions: (
        | NormalViewActionNodeLogT
        | WebHookNodeLogT
        | WebHookTriggerNodeLogT
        | EndNodeLogT
        | PollingNodeLogT
        | ConditionNodeLogT
        | ApprovalNodeLogT
        | TimerNodeLogT
        | NotificationNodeLogT
    )[]
}

export type DeveloperJobDataT = {
    jobId: string
    id: number
    node_execution_status: executionStatus
    input_schema: InputSchema[]
    output_schema: any
    node_meta: { job: number; loop: string }
    inserted_date: string
    actions: (ActionNodeLogT | WebHookNodeLogT | EndNodeLogT | PollingNodeLogT)[]
}

// export type InputSchema = {
//     key: string
//     displayTitle: string
//     description: string
//     required: boolean
//     config: {
//         kind: 'text' | 'enum'
//     }
//     value: string
// }

export type InputSchema = DefaultInputKeysT & { config: ConfigKinds & ConfigIdT }

export type LogsT = {
    type: string
    output: {
        normalView: (
            | NormalViewActionNodeLogT
            | WebHookNodeLogT
            | EndNodeLogT
            | PollingNodeLogT
            | ForEachNodeLogT
            | EventTriggerNodeLogT
        )[]
        developerView: (ActionNodeLogT | WebHookNodeLogT | EndNodeLogT | PollingNodeLogT | ForEachNodeLogT)[]
        status: 'in progress' | 'failed' | 'success'
    }
}

export type TriggerLogsT = {
    type: string
    output: {
        job: {
            normalView: (
                | NormalViewActionNodeLogT
                | WebHookNodeLogT
                | EndNodeLogT
                | PollingNodeLogT
                | ForEachNodeLogT
                | EventTriggerNodeLogT
            )[]
            developerView: (ActionNodeLogT | WebHookNodeLogT | EndNodeLogT | PollingNodeLogT | ForEachNodeLogT)[],
            executed_by:null|string,
            status: executionStatus | 'in progress'
        },
        status: executionStatus | 'in progress'
    }[]
}
export type ReactSetStateObject<T> = [T, React.Dispatch<React.SetStateAction<T>>]

export type AutomationMeta = {
    name: string
    description: string
    type: 'trigger' | 'no_trigger' | ''
    active_status: boolean
    deployed_status: string
    nodesWithoutConnection: string[]
    isLinkedToDialog: boolean
}
